import { isExpediaExactArticle, EXPEDIA_PRESENTATION } from "@/lib/expediaArticlePresentation";
import { canonicalPath } from "@/lib/canonicalPath";
import { isAiOverviewsExactArticle, AI_OVERVIEWS_PRESENTATION, AI_OVERVIEWS_AUDIT_COPY } from "@/lib/aiOverviewsArticlePresentation";
import { blogTopicArchivePath, getBlogTopicArchiveByTag } from "@/lib/blogTopicArchives";
import { Link, useParams } from "wouter";
import { useEffect, useState, useRef, useMemo, type ComponentProps, type HTMLAttributes, type ReactNode } from "react";
import { useLocation } from "wouter";
import { trackPageView, trackBlogShare, trackBlogShareClick, trackOutboundSocialClick, trackRelatedPostClick, trackBlogToolLinkClick, trackManusReferralClick, useScrollDepth } from "@/lib/analytics";
import { getPostBySlug, getRelatedPosts, blogPosts } from "@/data/blogPosts";
import { getBlogConversionPath, getPillarsForPost, getSiblingSpokes, getExternalReferences, getRelatedServicesForPost } from "@/data/contentArchitecture";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import SchemaScript from "@/components/SchemaScript";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/Breadcrumbs";
import { resolveSocialPreviewImage } from "@/lib/socialPreview";
import { Badge } from "@/components/ui/badge";
import { AnimatedStatGrid } from "@/components/AnimatedStat";
import { CalendarDays, User, ArrowLeft, ArrowRight, Clock, Share2, ExternalLink, BookOpen, Search, Linkedin, Twitter, Link2, Check, RefreshCw, MessageCircle } from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";
import RelatedPosts from "@/components/RelatedPosts";
import RelatedAIInsightsCarousel from "@/components/RelatedAIInsightsCarousel";
import RelatedNews from "@/components/RelatedNews";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import NewsletterSubscriptionForm from "@/components/NewsletterSubscriptionForm";
import { isNewsletterSubscriptionSource } from "@shared/newsletterSubscription";
import RelatedServicePathway from "@/components/RelatedServicePathway";
import RelatedServicesSection from "@/components/RelatedServicesSection";
import EvidenceSourceContext from "@/components/EvidenceSourceContext";
import AuthorBio from "@/components/AuthorBio";
import SharePostRow from "@/components/SharePostRow";
import SecurityNonAffiliationNotice from "@/components/SecurityNonAffiliationNotice";
import ReportSuspiciousInvitationCallout from "@/components/ReportSuspiciousInvitationCallout";
import MidArticleCard from "@/components/MidArticleCard";
import PersonalAgentControlsComparison from "@/components/PersonalAgentControlsComparison";
import SaudiAiGigaCampusInfographic from "@/components/SaudiAiGigaCampusInfographic";
import BuyerArgumentMatrix from "@/components/BuyerArgumentMatrix";
import ModelReleaseEvidenceVisuals from "@/components/ModelReleaseEvidenceVisuals";
import ChatGPTQueryVolumeVisuals, { QUERY_VOLUME_SLUG, type QueryVolumeVisualVariant } from "@/components/ChatGPTQueryVolumeVisuals";
import { MODEL_RELEASE_NEWS_SLUG, MODEL_RELEASE_EVIDENCE } from "@/data/modelReleaseVisualEvidence";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Streamdown, defaultRehypePlugins } from "streamdown";
import { GoldmanToolsComparison, IndustryChangeFigure, OccupationalOutlookFigure, PayrollForecastFigure } from "@/components/SeptemberPayrollFigures";
import AuditWidget from "@/components/AuditWidget";
import AEOSeriesNav, { AEO_SERIES_POSTS } from "@/components/AEOSeriesNav";
import { AEOLeadCaptureForm } from "@/components/AEOLeadCaptureForm";
import TokenCostCalculator from "@/components/TokenCostCalculator";
import ClusterPathway from "@/components/ClusterPathway";
import GEOReadinessAssessment, { type GEOAssessmentPayload } from "@/components/GEOReadinessAssessment";
import GEOShareToolbar from "@/components/GEOShareToolbar";
import TableOfContents from "@/components/TableOfContents";
import { isLongFormPillarArticle } from "@/lib/stickyPillarToc";
import { FloatingShareRail } from "@/components/FloatingShareRail";
import AEOScorecardLeadMagnet from "@/components/AEOScorecardLeadMagnet";
import USChinaAIBriefingLeadMagnet from "@/components/USChinaAIBriefingLeadMagnet";
import PPCOptimisationGuideLink from "@/components/PPCOptimisationGuideLink";
import B2BMarketingAgenciesGuideLink from "@/components/B2BMarketingAgenciesGuideLink";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import LocalIndustryGuideLinks, { type LocalIndustryContext } from "@/components/LocalIndustryGuideLinks";
import ArabicToggle from "@/components/ArabicToggle";
import { isPublishedBilingualArticlePath } from "@/i18n/localisedRoutes";
import { getBilingualRelatedServicesForPost } from "@/lib/relatedServices";
import { combineSourceContext, getArticleSourceContext } from "@/lib/articleSourceContext";
import {
  getRelatedArticleCarouselContext,
  resolveRelatedArticleCarouselSummaries,
  selectOlderRelatedInsights,
  shouldUseRelatedAIInsightsCarousel,
} from "@/lib/relatedAIInsights";
import { resolveDynamicRelatedPosts } from "@/lib/dynamicRelatedPosts";
import { getArticleSummaryPlacement, getTldrBulletSource } from "@/lib/articleTldrPlacement";
import {
  createTocHeadingIdResolver,
  getTocHeadingText,
  normalizeContentForToc,
  parseTocItems,
} from "@/lib/tableOfContents";
import {
  ARTICLE_FAQ_HEADING_HIERARCHY,
  ARTICLE_FAQ_SECTION_ID,
  createArticleFaqPageNode,
} from "@/lib/articleFaq";

const B2B_AGENCY_COMPARISON_RELATED_POSTS = new Set([
  "aeo-agency-pricing-uk-cost-roi-guide-2026",
  "agent-qualified-leads-aql-replace-mql-b2b-pipeline-2026",
  "organic-paid-agentic-ai-cpa-ltv",
]);

const LOCAL_INDUSTRY_RELATED_POSTS: ReadonlyMap<string, LocalIndustryContext> = new Map([
  ["ai-marketing-agency-credibility-test-2026", "general"],
  ["microsoft-clarity-topic-insights-ai-search-content-2026", "aeo"],
  ["voice-search-apple-qai-acquisition", "general"],
  ["aeo-2026-complete-guide-answer-engine-optimisation-b2b", "aeo"],
  ["b2b-ai-search-visibility-gap-ai-overviews-2026", "aeo"],
  ["ai-discoverability-pipeline-problem-b2b-revenue-2026", "general"],
]);

const ARTICLE_PROSE_CLASS = "prose prose-lg max-w-none prose-headings:font-serif prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2 prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-muted-foreground prose-table:text-sm prose-th:bg-muted prose-th:px-4 prose-th:py-2 prose-td:px-4 prose-td:py-2 prose-td:border-b prose-td:border-border";

// Streamdown's default hardening configuration allows trustworthy HTTP(S) links,
// but needs an origin to validate the site's canonical relative article links.
// Preserve its image and scheme protections rather than disabling the sanitizer.
const ARTICLE_REHYPE_PLUGINS = Object.entries(defaultRehypePlugins).map(([name, plugin]) => (
  name === "harden"
    ? [Array.isArray(plugin) ? plugin[0] : plugin, {
        defaultOrigin: "https://integrated.social",
        allowedLinkPrefixes: ["*"],
        allowedImagePrefixes: ["*"],
        allowDataImages: true,
      }]
    : plugin
)) as NonNullable<ComponentProps<typeof Streamdown>["rehypePlugins"]>;

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug || "");
  // GEO assessment results - lifted here so GEOShareToolbar can include them in the PDF
  const [geoAssessmentPayload, setGeoAssessmentPayload] = useState<GEOAssessmentPayload | null>(null);
  // Sentinel ref for FloatingShareRail - hides the rail when SharePostRow is visible
  const shareRowSentinelRef = useRef<HTMLDivElement | null>(null);

  // useScrollDepth replaces the manual scroll listener - fires GA4 milestone events
  // AND exposes current depth for CTA click handlers via scrollDepth.
  const scrollDepth = useScrollDepth({
    page_slug: slug || "",
    page_title: post?.title || "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Delegated click listener: fires trackBlogToolLinkClick when a reader clicks
  // any inline /tools/ link rendered inside the article body via Streamdown.
  // Uses event delegation on the document so it works regardless of when the
  // Streamdown content is hydrated. Cleans up on slug change (new post).
  useEffect(() => {
    const handleToolLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href.startsWith("/tools/")) return;
      // Only fire for links inside the article prose area (not nav or footer)
      const articleEl = document.querySelector("article.min-w-0");
      if (!articleEl || !articleEl.contains(anchor)) return;
      trackBlogToolLinkClick({
        tool_href: href,
        post_slug: post?.slug ?? "",
      });
    };
    document.addEventListener("click", handleToolLinkClick, { capture: true });
    return () => document.removeEventListener("click", handleToolLinkClick, { capture: true });
  }, [post?.slug]);

  if (!post) {
    // Extract keywords from the broken URL slug to suggest related articles
    const slugWords = (slug || "").replace(/-/g, " ").split(/\s+/).filter(w => w.length > 3);
    const matchedArticles = blogPosts
      .filter(p => {
        const titleLower = p.title.toLowerCase();
        const tagsLower = p.tags.map(t => t.toLowerCase()).join(" ");
        return slugWords.some(word => titleLower.includes(word) || tagsLower.includes(word));
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);

    // Fallback: show top 3 most recent posts if no keyword matches found
    const suggestedArticles = matchedArticles.length > 0
      ? matchedArticles
      : [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);

    const suggestionLabel = matchedArticles.length > 0
      ? "Based on the URL, you might be looking for:"
      : "Our most popular recent articles:";

    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#0F1419] pt-24 flex items-center justify-center px-4">
          <div className="max-w-2xl text-center">
            <div className="text-[#FF8C00] text-xs font-mono tracking-widest uppercase mb-4">
              Article Not Found
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl text-white mb-4">
              This article doesn't exist.
            </h1>
            <p className="text-white/60 text-lg mb-8">
              The article may have been moved, renamed, or removed. Try searching or browse our suggestions below.
            </p>

            {suggestedArticles.length > 0 && (
              <div className="mb-8 text-left">
                <p className="text-emerald-400 text-xs font-mono tracking-wider uppercase mb-3 text-center">
                  {suggestionLabel}
                </p>
                <div className="space-y-2">
                  {suggestedArticles.map((article) => (
                    <Link
                      key={article.slug}
                      href={`/blog/${article.slug}/`}
                      className="group flex items-start gap-3 bg-white/5 border border-white/10 rounded-sm p-3 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors"
                    >
                      <span className="text-emerald-400 text-lg mt-0.5">→</span>
                      <div>
                        <span className="text-white text-sm font-medium group-hover:text-emerald-400 transition-colors line-clamp-1">
                          {article.title}
                        </span>
                        <div className="flex gap-1.5 mt-1">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[9px] font-mono uppercase text-[#FF8C00]/70 bg-[#FF8C00]/10 px-1.5 py-0.5 rounded-sm">
                              {tag}
                            </span>
                          ))}
                          <span className="text-white/30 text-[9px]">{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/blog/" className="bg-[#FF8C00] text-[#0F1419] px-6 py-3 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors">
                Browse All Articles
              </Link>
              <Link href="/blog/search/" className="bg-white/10 text-white px-6 py-3 text-sm font-semibold rounded-sm hover:bg-white/20 transition-colors border border-white/20">
                Search Articles
              </Link>
              <Link href="/" className="text-white/60 px-6 py-3 text-sm hover:text-white transition-colors border border-white/10 rounded-sm hover:border-white/30">
                Homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const exactAiOverviews = isAiOverviewsExactArticle(post.slug);
  const exactExpedia = isExpediaExactArticle(post.slug);
  const exactEditorial = exactAiOverviews || exactExpedia;
  const exactPresentation = exactExpedia ? EXPEDIA_PRESENTATION : AI_OVERVIEWS_PRESENTATION;

  // Related Articles remain curated first, then dynamically fill any spare cards
  // from current same-topic and keyword-adjacent published articles. A narrow
  // editorial series can deliberately cap the module at its curated set.
  const relatedPosts = resolveDynamicRelatedPosts(post, blogPosts, post.relatedPostLimit ?? 4);
  const articleCarouselContext = getRelatedArticleCarouselContext(post.slug);
  const articleCarouselPosts = articleCarouselContext
    ? shouldUseRelatedAIInsightsCarousel(post.slug)
      ? selectOlderRelatedInsights({
          currentPost: post,
          directPosts: getRelatedPosts(post),
          allPosts: blogPosts,
        })
      : relatedPosts
    : [];
  const articleCarouselSummaries = articleCarouselContext
    ? resolveRelatedArticleCarouselSummaries(post, articleCarouselPosts)
    : {};
  const summaryPlacement = getArticleSummaryPlacement(post.slug);
  const relatedNewsPosts = (post.relatedNewsSlugs ?? [])
    .map((relatedSlug) => blogPosts.find((candidate) => candidate.slug === relatedSlug))
    .filter((candidate): candidate is (typeof blogPosts)[number] => Boolean(candidate))
    .filter((candidate) => !relatedPosts.some((related) => related.slug === candidate.slug))
    .slice(0, 3);
  const readingTime = Math.ceil(post.content.split(/\s+/).length / 200);
  const hasInteractiveArticleMedia = post.slug === "america-gov-phase-1-launch-gemini-and-phase-2";
  const articleAmazonProducts = useMemo(
    () => getProductsForArticle({
      slug: post.slug,
      tags: post.tags,
      title: post.title,
      content: post.content,
    }),
    [post.slug, post.tags, post.title, post.content],
  );

  // Convert legacy HTML H2/H3 tags to Markdown once so historic and current posts
  // share an identical heading, anchor and interactive-table-of-contents contract.
  const articleContent = useMemo(() => (
    normalizeContentForToc(post.content).replace(
      /\]\((https:\/\/integrated\.social)?(\/[^)\s]+)\)/g,
      (_match, _origin: string | undefined, target: string) => `](${canonicalPath(target)})`,
    )
  ), [post.content]);
  const highlightJumpLinks = useMemo(
    () => parseTocItems(post.content).filter((item) => item.level === 2).slice(0, 6),
    [post.content],
  );
  const renderArticleByline = () => (
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-y border-border py-4">
            <Link href="/modi-elnadi/" className="flex items-center gap-1.5 hover:text-[#FF8C00] transition-colors">
              <User className="w-4 h-4" />
              <span className="font-medium text-foreground">{post.author}</span>
            </Link>
            <span className="flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </time>
            </span>
            {(post.dateModified ?? post.updatedDate) && (post.dateModified ?? post.updatedDate) !== post.date && (
              <span className="flex items-center gap-1.5 text-amber-600">
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Updated <time dateTime={post.dateModified ?? post.updatedDate}>{new Date(post.dateModified ?? post.updatedDate!).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</time></span>
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {readingTime} min read
            </span>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: post.title, url: window.location.href });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              className="ml-auto flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
  );

  const renderArticleMedia = () => (<>
        {/* Audio Player - sits between header and hero image */}
        <div className="container max-w-4xl mx-auto px-4 pb-8">
          <AudioPlayer
            text={post.content}
            title={post.title}
            readingTime={readingTime}
          />
        </div>

        {/* Hero Image */}
        <div className="container max-w-4xl mx-auto px-4 mb-12">
          <figure>
          <div className={`group aspect-[16/9] overflow-hidden rounded-xl bg-muted ${hasInteractiveArticleMedia ? "ring-1 ring-transparent transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 hover:ring-[#00D4AA]/35 hover:shadow-[0_18px_42px_rgba(0,212,170,0.16)] motion-reduce:transform-none motion-reduce:transition-none" : ""}`}>
            <ResponsiveImage
              src={post.heroImage}
              alt={post.heroAlt || post.title}
              className={`w-full h-full object-cover ${hasInteractiveArticleMedia ? "transition-transform duration-500 ease-out group-hover:scale-[1.02] motion-reduce:transform-none motion-reduce:transition-none" : ""}`}
              width={post.slug === QUERY_VOLUME_SLUG ? 1600 : 1200}
              height={post.slug === QUERY_VOLUME_SLUG ? 900 : 675}
              sizes="(min-width: 1280px) 896px, (min-width: 768px) calc(100vw - 48px), calc(100vw - 32px)"
              priority
            />
          </div>
          {post.heroCaption && (
            <figcaption className="border-x border-b border-border bg-muted/30 px-5 py-3 text-sm leading-relaxed text-muted-foreground">
              {post.heroCaption}
            </figcaption>
          )}
          </figure>
        </div>

  </>);

  const renderAiSummary = () => {
    if (!post.aiSummary?.length) return null;

    const isTopTldr = summaryPlacement === "top";
    const summarySources = getArticleSourceContext(post.slug);
    const hasClaimLevelSource = post.aiSummary.some((_point, index) =>
      Boolean(getTldrBulletSource(post.slug, index, summarySources)),
    );
    return (
      <section
        className="ai-summary-block rounded-xl border border-[#00D4AA]/30 bg-[#00D4AA]/5 px-6 py-5"
        aria-labelledby="article-tldr-heading"
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#00D4AA]/20 text-[#00D4AA] uppercase tracking-wider">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            {isTopTldr ? "TL;DR" : "AI Summary"}
          </span>
          <h2 id="article-tldr-heading" className="text-xs font-semibold text-foreground">
            {isTopTldr ? "Main takeaways" : "Key takeaways for AI answer engines"}
          </h2>
        </div>
        {(isTopTldr || hasClaimLevelSource) && (
          <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
            {isTopTldr
              ? "Source-qualified AI-generated takeaways, reviewed against the article’s cited reporting."
              : "Links appear only where a listed source directly supports the individual takeaway; editorial analysis and recommendations remain clearly uncited."}
          </p>
        )}
        <ul className="space-y-3">
          {post.aiSummary.map((point, i) => {
            const source = getTldrBulletSource(
              post.slug,
              i,
              summarySources,
            );

            return (
              <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#00D4AA]" aria-hidden="true" />
                <div className="min-w-0">
                  <p>{point}</p>
                  {source && (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="article-tldr-source-link"
                      aria-label={`Read source for takeaway ${i + 1}: ${source.title}`}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#008f75] underline-offset-2 hover:text-[#006e5a] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4AA] dark:text-[#39e2bf] dark:hover:text-[#77f0d4]"
                    >
                      Source: {source.title}
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {highlightJumpLinks.length > 0 && (
          <nav aria-label="Jump to the full guide" className="mt-5 border-t border-[#00D4AA]/20 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-foreground">
              Jump to the full guide
            </p>
            <div className="flex flex-wrap gap-2">
              {highlightJumpLinks.map((item, index) => (
                <a
                  key={item.id}
                  href={`#${encodeURIComponent(item.id)}`}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[#00D4AA]/30 bg-background/60 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-[#00D4AA] hover:bg-[#00D4AA]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4AA]"
                >
                  <span className="text-[#00A98A]" aria-hidden="true">{index + 1}</span>
                  <span className="truncate">{item.text}</span>
                </a>
              ))}
            </div>
          </nav>
        )}
      </section>
    );
  };

  // Split the article content at the third H2 heading for mid-article card injection.
  // Returns [beforeThirdH2, fromThirdH2] - if fewer than 3 H2s exist, second element is empty.
  const [contentBefore, contentAfter] = (() => {
    const lines = articleContent.split("\n");
    let h2Count = 0;
    let splitIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      if (/^## /.test(lines[i])) {
        h2Count++;
        if (h2Count === 3) {
          splitIndex = i;
          break;
        }
      }
    }
    if (exactEditorial) return [articleContent, ""];
    if (splitIndex === -1) return [articleContent, ""];
    return [
      lines.slice(0, splitIndex).join("\n"),
      lines.slice(splitIndex).join("\n"),
    ];
  })();

  // A single resolver is intentionally shared across the first/second content
  // fragments so duplicate headings receive deterministic, unique HTML IDs.
  type TocHeadingRendererProps = HTMLAttributes<HTMLHeadingElement> & { children?: ReactNode };
  type ArticleImageRendererProps = ComponentProps<"img">;
  const tocHeadingIdResolver = createTocHeadingIdResolver();
  const renderHeadingAliases = (id: string | null | undefined) => {
    if (!exactEditorial || !id) return null;
    const aliases = (exactPresentation.aliases as Record<string, readonly string[]>)[id];
    return aliases?.map((alias) => <span key={alias} id={alias} data-legacy-heading-alias="true" aria-hidden="true" className="block h-0 scroll-mt-28" />);
  };
  const tocHeadingComponents = {
    h2: ({ children, id: _ignoredId, ...props }: TocHeadingRendererProps) => {
      const id = tocHeadingIdResolver(2, getTocHeadingText(children));
      return <>{renderHeadingAliases(id)}<h2 id={id ?? undefined} tabIndex={id ? -1 : undefined} className="scroll-mt-28" {...props}>{children}</h2></>;
    },
    h3: ({ children, id: _ignoredId, ...props }: TocHeadingRendererProps) => {
      const id = tocHeadingIdResolver(3, getTocHeadingText(children));
      return <>{renderHeadingAliases(id)}<h3 id={id ?? undefined} tabIndex={id ? -1 : undefined} className="scroll-mt-28" {...props}>{children}</h3></>;
    },
    h4: ({ children, id: _ignoredId, ...props }: TocHeadingRendererProps) => {
      const id = tocHeadingIdResolver(4, getTocHeadingText(children));
      return <>{renderHeadingAliases(id)}<h4 id={id ?? undefined} tabIndex={id ? -1 : undefined} className="scroll-mt-28" {...props}>{children}</h4></>;
    },
    img: ({ src, alt, width, height, ...props }: ArticleImageRendererProps) => {
      if (typeof src !== "string") return null;
      const resolvedWidth = typeof width === "number" ? width : Number.parseInt(String(width ?? "1200"), 10) || 1200;
      const resolvedHeight = typeof height === "number" ? height : Number.parseInt(String(height ?? "675"), 10) || 675;
      return (
        <ResponsiveImage
          src={src}
          alt={alt ?? ""}
          width={resolvedWidth}
          height={resolvedHeight}
          sizes="(min-width: 1280px) 896px, (min-width: 768px) calc(100vw - 48px), calc(100vw - 32px)"
          {...props}
        />
      );
    },
  };

  const renderArticleSegment = (markdown: string, keyPrefix: string): ReactNode => {
    if (post.slug === QUERY_VOLUME_SLUG) {
      const pattern = /(<div data-query-volume-visual="(?:evidence|ladder|journey|plan)"><\/div>)/g;
      return markdown.split(pattern).filter(Boolean).map((part, index) => {
        const matched = part.match(/^<div data-query-volume-visual="(evidence|ladder|journey|plan)"><\/div>$/);
        if (matched) {
          return <ChatGPTQueryVolumeVisuals key={`${keyPrefix}-query-visual-${index}`} variant={matched[1] as QueryVolumeVisualVariant} />;
        }
        return <div className={ARTICLE_PROSE_CLASS} key={`${keyPrefix}-query-copy-${index}`}><Streamdown components={tocHeadingComponents} rehypePlugins={ARTICLE_REHYPE_PLUGINS}>{part}</Streamdown></div>;
      });
    }
    if (post.slug === MODEL_RELEASE_NEWS_SLUG) {
      const pattern = /(<div data-model-release-visual="(?:evidence|cost|scorecard)"><\/div>)/g;
      return markdown.split(pattern).filter(Boolean).map((part, index) => {
        const matched = part.match(/^<div data-model-release-visual="(evidence|cost|scorecard)"><\/div>$/);
        if (matched) {
          const variant = matched[1] as "evidence" | "cost" | "scorecard";
          return <div className="not-prose" key={`${keyPrefix}-model-visual-${index}`}><ModelReleaseEvidenceVisuals variant={variant} evidence={MODEL_RELEASE_EVIDENCE} /></div>;
        }
        return <div className={ARTICLE_PROSE_CLASS} key={`${keyPrefix}-model-copy-${index}`}><Streamdown components={tocHeadingComponents} rehypePlugins={ARTICLE_REHYPE_PLUGINS}>{part}</Streamdown></div>;
      });
    }
    if (post.slug !== "us-september-2026-payrolls-ai-finance-jobs") {
      return <div className={ARTICLE_PROSE_CLASS}><Streamdown components={tocHeadingComponents} rehypePlugins={ARTICLE_REHYPE_PLUGINS}>{markdown}</Streamdown></div>;
    }

    const figures: Record<string, ReactNode> = {
      "<!-- SeptemberPayroll:Forecast -->": <PayrollForecastFigure />,
      "<!-- SeptemberPayroll:Industry -->": <IndustryChangeFigure />,
      "<!-- SeptemberPayroll:Outlook -->": <OccupationalOutlookFigure />,
      "<!-- SeptemberPayroll:Goldman -->": <GoldmanToolsComparison />,
    };
    const markerPattern = /(<!-- SeptemberPayroll:(?:Forecast|Industry|Outlook|Goldman) -->)/g;

    return markdown.split(markerPattern).filter(Boolean).map((part, index) => {
      const figure = figures[part];
      if (figure) return <div className="not-prose" key={`${keyPrefix}-figure-${index}`}>{figure}</div>;
      return <div className={ARTICLE_PROSE_CLASS} key={`${keyPrefix}-copy-${index}`}><Streamdown components={tocHeadingComponents} rehypePlugins={ARTICLE_REHYPE_PLUGINS}>{part}</Streamdown></div>;
    });
  };

  // Pick the best mid-article related post: first related post that shares a tag with the current article
  const midArticlePost = relatedPosts[0] ?? null;
  const midArticleSharedTag = midArticlePost
    ? midArticlePost.tags.find((t) => post.tags.includes(t)) ?? midArticlePost.tags[0]
    : null;

  // Pillar-spoke data
  const pillars = getPillarsForPost(post.slug);
  const shouldUseStickyPillarToc = !exactEditorial && isLongFormPillarArticle(post);
  const conversionPath = getBlogConversionPath(post.slug, post.tags);
  const siblingSpokeSlugs = getSiblingSpokes(post.slug);
  const relatedServices = getBilingualRelatedServicesForPost(post.slug, post.tags);
  const usesBottomRelatedServices = isPublishedBilingualArticlePath(`/blog/${post.slug}/`);
  const externalRefs = exactExpedia ? getArticleSourceContext(post.slug) : combineSourceContext(
    getArticleSourceContext(post.slug),
    getExternalReferences(post.slug),
    conversionPath.service?.externalReferences ?? [],
  );
  const siblingPosts = siblingSpokeSlugs
    .map((s) => blogPosts.find((p) => p.slug === s))
    .filter(Boolean)
    .slice(0, 4);

  // ─── COMPREHENSIVE AEO SCHEMA ────────────────────────────────────────────────
  // Schema types: NewsArticle, Person (author), BreadcrumbList, FAQPage,
  // Service references, speakable, mentions, isBasedOn (E-E-A-T)
  // ─────────────────────────────────────────────────────────────────────────────

  // Determine article type: NewsArticle for timely AI news/trends, BlogPosting for evergreen guides
  // Includes "AI News & Trends" (used by Agentforce + Zero-Click posts) and legacy tag variants
  const isNewsArticle = post.tags.some((t) =>
    [
      "AI News & Trends",
      "Tech News & Trends",
      "AI Startups & Funding",
      "Nasdaq & AI Stocks",
      "Aerospace & AI",
      "Google AI Overviews",
      "ai-news-trends",
    ].includes(t)
  );
  const articleType = post.schemaType ?? (isNewsArticle ? "NewsArticle" : "BlogPosting");
  const schemaHeroImage = post.heroImage.startsWith("http")
    ? post.heroImage
    : `https://integrated.social${post.heroImage}`;

  // Author Person schema, rich entity graph for E-E-A-T
  const authorSchema = {
    "@type": "Person",
    "@id": "https://integrated.social/modi-elnadi/#person",
    name: "Modi Elnadi",
    url: "https://integrated.social/modi-elnadi/",
    image: "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
    jobTitle: "Director of Marketing & AI Growth | Founder, Integrated.Social",
    description: post.authorSchemaDescription ?? "16+ years deploying marketing systems that generate measurable revenue. Early adopter of agentic AI for B2B lead generation, GTM automation, and multi-agent orchestration.",
    sameAs: post.authorSameAs ?? [
      "https://www.linkedin.com/in/modielnadi/",
      "https://x.com/MoElnadi",
      "https://integrated.social/modi-elnadi/",
    ],
    knowsAbout: [
      "Agentic AI",
      "AI Search",
      "AI Marketing",
      "SEO (Search Engine Optimisation)",
      "AEO (Answer Engine Optimisation)",
      "GEO (Generative Engine Optimisation)",
      "ABM (Account-Based Marketing)",
      "B2B Marketing",
      "B2B2C Marketing",
      "B2C Marketing",
      "Performance Marketing",
      "PPC (Pay-Per-Click Advertising)",
      "Go-to-Market (GTM) Strategy",
      "Content Strategy",
      "Large Language Models (LLMs)",
      "Prompt Engineering",
      "Answer Engine Optimization",
      "Generative Engine Optimization",
      "AI Search Visibility",
      "B2B Lead Generation",
      "Google Ads & Performance Max",
      "Performance Max Campaigns",
      "Account-Based Marketing",
      "AI Marketing Strategy",
      "Multi-Agent AI Orchestration",
      "Gemini Enterprise",
      "OpenAI GPT Enterprise",
      "Anthropic Claude Enterprise",
      "AI Content Operations",
      "Paid Media Strategy",
      "Marketing Automation",
      "GTM Automation",
      "Conversion Rate Optimisation",
      "B2B Pipeline Generation",
      "Fintech Marketing",
      "SaaS Marketing",
      "Open-Weight AI Models",
      "Enterprise AI Architecture",
      "Data Sovereignty in AI",
    ],
    hasCredential: [
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Professional Experience",
        name: "16+ years B2B marketing leadership",
        description: "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA.",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Specialisation",
        name: "Agentic AI for B2B Growth",
        description: "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023.",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Specialisation",
        name: "Answer Engine Optimisation (AEO) and GEO",
        description: "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode.",
      },
    ],
    worksFor: { "@id": "https://integrated.social/#organization" },
  };

  // Publisher Organisation schema
  const publisherSchema = {
    "@type": "Organization",
    "@id": "https://integrated.social/#organization",
    name: "Integrated.Social",
    legalName: "Integrated Social Ltd",
    url: "https://integrated.social/",
    logo: {
      "@type": "ImageObject",
      url: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
      width: 457,
      height: 457,
    },
    sameAs: [
      "https://www.linkedin.com/company/integrated-social-ltd",
      "https://x.com/MoElnadi",
    ],
  };

  // Service references mirror the visible related-service pathway through the valid Article.about property.
  const serviceSchemaTopics = [...pillars, ...(conversionPath.service ? [conversionPath.service] : [])]
    .filter((p, index, all) => p.slug.startsWith("/services/") && all.findIndex((candidate) => candidate.slug === p.slug) === index);
  const serviceSchemas = (exactEditorial ? [] : serviceSchemaTopics).map((p) => {
    const serviceUrl = `https://integrated.social${p.slug.replace(/\/$/, "")}/`;
    return {
      "@type": "Service",
      "@id": `${serviceUrl}#service`,
      name: p.name,
      description: p.description,
      url: serviceUrl,
      provider: { "@id": "https://integrated.social/#organization" },
    };
  });

  // ── Per-post mentions entities ──────────────────────────────────────────────
  // Derived from post tags + content: named organisations/sources cited in the article.
  // These boost entity graph richness and E-E-A-T signals for both posts.
  const mentionsMap: Record<string, { name: string; url: string; type: string }[]> = {
    "salesforce-agentforce-pay-per-resolution-enterprise-ai-pricing-2026": [
      { name: "Salesforce", url: "https://www.salesforce.com", type: "Organization" },
      { name: "Futurum Group", url: "https://futurumgroup.com", type: "Organization" },
      { name: "Microsoft", url: "https://www.microsoft.com", type: "Organization" },
      { name: "ServiceNow", url: "https://www.servicenow.com", type: "Organization" },
      { name: "Zendesk", url: "https://www.zendesk.com", type: "Organization" },
      { name: "Agentforce", url: "https://www.salesforce.com/agentforce/", type: "SoftwareApplication" },
    ],
    "zero-click-commerce-b2b-ai-agents-2026": [
      { name: "Gartner", url: "https://www.gartner.com", type: "Organization" },
      { name: "Adobe Analytics", url: "https://business.adobe.com/products/analytics/adobe-analytics.html", type: "SoftwareApplication" },
      { name: "BCG", url: "https://www.bcg.com", type: "Organization" },
      { name: "J.P. Morgan", url: "https://www.jpmorgan.com", type: "Organization" },
      { name: "Digital Sales Institute", url: "https://www.digitalsalesinstitute.com", type: "Organization" },
      { name: "Grand View Research", url: "https://www.grandviewresearch.com", type: "Organization" },
    ],
    "chatgpt-vs-gemini-vs-claude-vs-manus-ai-assistant-comparison-2026": [
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "Google Gemini", url: "https://gemini.google.com", type: "SoftwareApplication" },
      { name: "Claude", url: "https://claude.ai", type: "SoftwareApplication" },
      { name: "Manus", url: "https://manus.im", type: "SoftwareApplication" },
      { name: "OpenAI", url: "https://openai.com", type: "Organization" },
      { name: "Google DeepMind", url: "https://deepmind.google", type: "Organization" },
      { name: "Anthropic", url: "https://www.anthropic.com", type: "Organization" },
    ],
    "google-ai-business-agent-search-ads-lead-forms-2026": [
      { name: "Google", url: "https://www.google.com", type: "Organization" },
      { name: "Google Ads", url: "https://ads.google.com", type: "SoftwareApplication" },
      { name: "Google AI Mode", url: "https://blog.google/products/search/google-ai-mode/", type: "SoftwareApplication" },
      { name: "Gartner", url: "https://www.gartner.com", type: "Organization" },
      { name: "WordStream", url: "https://www.wordstream.com", type: "Organization" },
    ],
    "ai-engines-store-brands-vs-national-brands-citation-share-2026": [
      { name: "Perplexity", url: "https://www.perplexity.ai", type: "SoftwareApplication" },
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "Google Gemini", url: "https://gemini.google.com", type: "SoftwareApplication" },
      { name: "Nielsen", url: "https://www.nielsen.com", type: "Organization" },
      { name: "McKinsey", url: "https://www.mckinsey.com", type: "Organization" },
    ],
    "ai-search-replacing-google-or-making-it-more-powerful-2026": [
      { name: "Google", url: "https://www.google.com", type: "Organization" },
      { name: "Perplexity", url: "https://www.perplexity.ai", type: "SoftwareApplication" },
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "Gartner", url: "https://www.gartner.com", type: "Organization" },
      { name: "SparkToro", url: "https://sparktoro.com", type: "Organization" },
      { name: "Similarweb", url: "https://www.similarweb.com", type: "SoftwareApplication" },
    ],
    "full-funnel-aeo-ai-visibility-to-revenue-2026": [
      { name: "Perplexity", url: "https://www.perplexity.ai", type: "SoftwareApplication" },
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "Google Gemini", url: "https://gemini.google.com", type: "SoftwareApplication" },
      { name: "Claude", url: "https://claude.ai", type: "SoftwareApplication" },
      { name: "Manus", url: "https://manus.im", type: "SoftwareApplication" },
      { name: "Gartner", url: "https://www.gartner.com", type: "Organization" },
      { name: "Forrester", url: "https://www.forrester.com", type: "Organization" },
      { name: "McKinsey", url: "https://www.mckinsey.com", type: "Organization" },
    ],
    "chatgpt-work-agentic-ai-marketing-workflows-2026": [
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "OpenAI", url: "https://openai.com", type: "Organization" },
      { name: "Microsoft Copilot", url: "https://copilot.microsoft.com", type: "SoftwareApplication" },
      { name: "Google Gemini", url: "https://gemini.google.com", type: "SoftwareApplication" },
      { name: "BCG", url: "https://www.bcg.com", type: "Organization" },
      { name: "McKinsey", url: "https://www.mckinsey.com", type: "Organization" },
    ],
    "zero-click-searches-how-to-win": [
      { name: "Google", url: "https://www.google.com", type: "Organization" },
      { name: "Perplexity", url: "https://www.perplexity.ai", type: "SoftwareApplication" },
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "SparkToro", url: "https://sparktoro.com", type: "Organization" },
      { name: "Semrush", url: "https://www.semrush.com", type: "SoftwareApplication" },
    ],
    "openai-70bn-revenue-run-rate-ipo-economics-2026": [
      { name: "OpenAI", url: "https://openai.com", type: "Organization" },
      { name: "ChatGPT", url: "https://chatgpt.com", type: "SoftwareApplication" },
      { name: "Reuters", url: "https://www.reuters.com", type: "Organization" },
      { name: "Bloomberg", url: "https://www.bloomberg.com", type: "Organization" },
    ],
  };
  const postMentions = [...(mentionsMap[post.slug] ?? []), ...(post.schemaMentions ?? [])].map((m) => ({
    "@type": m.type,
    name: m.name,
    url: m.url,
  }));

  const articleAbout = [...pillars, ...(post.schemaAbout ?? [])];

  // External references for isBasedOn (E-E-A-T / citation signals). The Gulf
  // comparison has six material primary announcements, all of which remain
  // visible and cited in its structured article graph.
  const articleSourceRefs = post.allSourceCitations
    ? getArticleSourceContext(post.slug)
    : externalRefs.slice(0, 5);
  const isBasedOn = articleSourceRefs.map((ref) => ({
    "@type": "WebPage",
    name: ref.title,
    url: ref.url,
  }));

  // BreadcrumbList, Home > Blog > [Pillar if exists] > Post Title
  const breadcrumbItems: object[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://integrated.social/" },
    { "@type": "ListItem", position: 2, name: "Blog", item: "https://integrated.social/blog/" },
  ];
  if (pillars.length > 0 && pillars[0].slug.startsWith("/services/")) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 3,
      name: pillars[0].name,
      item: `https://integrated.social${pillars[0].slug}/`,
    });
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 4,
      name: post.title,
      item: `https://integrated.social/blog/${post.slug}/`,
    });
  } else {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 3,
      name: post.title,
      item: `https://integrated.social/blog/${post.slug}/`,
    });
  }
  const visualBreadcrumbItems: BreadcrumbItem[] = [
    { label: "Blog", href: "/blog/" },
    ...(pillars.length > 0 && pillars[0].slug.startsWith("/services/")
      ? [{ label: pillars[0].name, href: canonicalPath(pillars[0].slug) }]
      : []),
    { label: post.title },
  ];

  // Main article schema (NewsArticle or BlogPosting)
  // Future editorial packages retain a literal source token until their scheduled
  // release. JSON-LD itself still needs a valid ISO date when the page is live.
  const schemaDateModified = post.dateModified === "{{DATE_MODIFIED}}"
    ? post.date
    : post.dateModified ?? post.updatedDate ?? post.date;

  const articleSchemaNode: Record<string, unknown> = {
    "@type": articleType,
    "@id": `https://integrated.social/blog/${post.slug}/#article`,
    headline: post.h1 ?? post.title,
    name: post.h1 ?? post.title,
    description: post.metaDescription,
    image: {
      "@type": "ImageObject",
      url: schemaHeroImage,
      width: post.slug === QUERY_VOLUME_SLUG ? 1600 : 1200,
      height: post.slug === QUERY_VOLUME_SLUG ? 900 : 675,
    },
    author: { "@id": authorSchema["@id"] },
    publisher: { "@id": publisherSchema["@id"] },
    isAccessibleForFree: true,
    datePublished: post.datePublished ?? post.date,
    dateModified: schemaDateModified,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://integrated.social/blog/${post.slug}/`,
    },
    url: `https://integrated.social/blog/${post.slug}/`,
    keywords: post.tags.join(", "),
    articleSection: post.tags[0],
    wordCount: post.content.split(/\s+/).length,
    inLanguage: post.locale ?? "en-GB",
    isPartOf: {
      "@type": "Blog",
      "@id": "https://integrated.social/blog/#blog",
      name: "AI Marketing & AEO Insights",
      url: "https://integrated.social/blog/",
    },
    about: [
      ...articleAbout.map((p) => ({
        "@type": "Thing",
        name: p.name,
        description: p.description,
      })),
      ...serviceSchemas,
    ],
    // isBasedOn, external authoritative sources (E-E-A-T signal)
    ...(isBasedOn.length > 0 && { isBasedOn }),
    // citation mirrors the visible evidence-and-source context section.
    ...(isBasedOn.length > 0 && { citation: isBasedOn }),
    // mentions, named organisations/sources cited - entity graph richness
    ...(postMentions.length > 0 && { mentions: postMentions }),
    // speakable, marks direct-answer paragraphs for voice search & AI Overviews
    // When aiSummary is present, also target the .ai-summary-block for explicit AI crawler coverage
    ...(!post.excludeSpeakable && { speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [
        ".article-intro",
        ".article-faq",
        "h1 + p",
        "h2 + p",
        ...(post.aiSummary && post.aiSummary.length > 0 ? [".ai-summary-block"] : []),
      ],
    } }),
    // abstract: machine-readable summary of the article for AI crawlers (Perplexity, Claude, ChatGPT)
    // Populated from aiSummary bullets when present, otherwise falls back to metaDescription
    abstract: post.aiSummary && post.aiSummary.length > 0
      ? post.aiSummary.join(" ")
      : post.metaDescription,
    // hasPart: explicitly marks the AI Summary block as a citable, structured excerpt
    ...(post.aiSummary && post.aiSummary.length > 0 && {
      hasPart: {
        "@type": "ItemList",
        "@id": `https://integrated.social/blog/${post.slug}/#ai-summary`,
        name: "AI Summary",
        description: "Key takeaways structured for answer engine citation",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: post.aiSummary.length,
        itemListElement: post.aiSummary.map((point, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: point,
        })),
      },
    }),
  };

  const articleStageSchemaNode = post.articleStages && post.articleStages.length > 0 ? {
    "@type": "ItemList",
    "@id": `https://integrated.social/blog/${post.slug}/#stages`,
    name: "Article stages",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: post.articleStages.length,
    itemListElement: post.articleStages.map((stage, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: stage.name,
      description: stage.description,
      url: `https://integrated.social/blog/${post.slug}/#${stage.id}`,
    })),
  } : null;

  // NewsArticle-specific fields
  if (articleType === "NewsArticle") {
    (articleSchemaNode as Record<string, unknown>).dateline = "London, UK";
    (articleSchemaNode as Record<string, unknown>).printSection = post.tags[0];
    // thumbnailUrl for Google News / Discover eligibility
    if (post.heroImage) {
      (articleSchemaNode as Record<string, unknown>).thumbnailUrl = schemaHeroImage;
    }
  }

  // The same post.faqSchema collection renders visible H3 question headings
  // and the FAQPage mainEntity, so schema cannot silently drift from readers.
  const faqSchemaNode = createArticleFaqPageNode({
    slug: post.slug,
    title: post.title,
    items: post.faqSchema,
  });

  const websiteSchema = {
    "@type": "WebSite",
    "@id": "https://integrated.social/#website",
    name: "Integrated.Social",
    url: "https://integrated.social/",
    publisher: { "@id": publisherSchema["@id"] },
  };

  // Full @graph schema block
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      articleSchemaNode,
      {
        "@type": "BreadcrumbList",
        "@id": `https://integrated.social/blog/${post.slug}/#breadcrumb`,
        itemListElement: breadcrumbItems,
      },
      // Standalone Person entity for author (separate from inline author above)
      {
        ...authorSchema,
        "@id": "https://integrated.social/modi-elnadi/#person",
      },
      // Standalone Organisation entity
      publisherSchema,
      ...(post.singleGraphSchema && articleStageSchemaNode ? [articleStageSchemaNode] : []),
      ...(post.singleGraphSchema && faqSchemaNode ? [faqSchemaNode] : []),
      ...(post.singleGraphSchema ? [websiteSchema] : []),
    ],
  };

  // ── DefinedTerm schema ─────────────────────────────────────────────────────
  // AI engines cite DefinedTerm verbatim in definition queries ("What is AEO?").
  // Triggered for posts that define key AEO/GEO/AI terminology.
  const definedTermsMap: Record<string, { name: string; description: string }[]> = {
    "zero-click-searches-how-to-win": [
      { name: "Zero-Click Search", description: "A search query where the user's question is answered directly on the search results page - by a featured snippet, AI Overview, knowledge panel, or People Also Ask box - without the user clicking through to any website." },
      { name: "Answer Engine Optimization (AEO)", description: "The practice of structuring content so that AI answer engines (ChatGPT, Perplexity, Google AI Overviews, Gemini) cite it as a direct answer to user queries, without requiring a click to the source page." },
    ],
    "full-funnel-aeo-ai-visibility-to-revenue-2026": [
      { name: "Answer Engine Optimization (AEO)", description: "The practice of structuring content, schema markup, and entity signals so that AI answer engines cite a brand as the authoritative source for specific queries across the full buyer journey." },
      { name: "Generative Engine Optimization (GEO)", description: "A content strategy discipline focused on making brand content retrievable and citable by large language model-powered search engines such as Perplexity, ChatGPT Search, and Google AI Mode." },
      { name: "AI Citation Funnel", description: "The staged progression from AI Mention through AI Recommendation to AI Conversion, used to measure AEO programme ROI across the full buyer journey." },
    ],
    "ai-search-replacing-google-or-making-it-more-powerful-2026": [
      { name: "AI Search", description: "A search paradigm where large language models synthesise answers from multiple sources and present them conversationally, rather than returning a ranked list of links. Examples include Perplexity, ChatGPT Search, and Google AI Mode." },
      { name: "Generative Engine Optimization (GEO)", description: "The discipline of structuring content so it is retrieved and cited by AI-powered search engines rather than ranked in traditional blue-link results pages." },
    ],
    "chatgpt-vs-gemini-vs-claude-vs-manus-ai-assistant-comparison-2026": [
      { name: "Agentic AI", description: "An AI system that can autonomously plan, execute multi-step tasks, use external tools, browse the web, write and run code, and produce finished deliverables without continuous human prompting." },
      { name: "Large Language Model (LLM)", description: "A deep learning model trained on large text corpora that can generate, summarise, translate, and reason about natural language. Examples include GPT-4o (OpenAI), Gemini 2.5 Pro (Google), Claude 4 (Anthropic), and the models powering Manus." },
    ],
    "google-ai-business-agent-search-ads-lead-forms-2026": [
      { name: "Google AI Business Agent", description: "A proposed Google product that would allow AI agents to complete commercial transactions - bookings, purchases, lead submissions - directly within Google Search on behalf of users, bypassing advertiser landing pages." },
      { name: "Performance Max (PMax)", description: "Google's fully automated campaign type that uses machine learning to serve ads across all Google channels - Search, Shopping, Display, YouTube, Gmail, and Maps - optimised toward a single conversion goal." },
    ],
  };
  const postDefinedTerms = definedTermsMap[post.slug];
  const definedTermSchema = postDefinedTerms && postDefinedTerms.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": `https://integrated.social/blog/${post.slug}/#glossary`,
    name: `Key Terms: ${post.title}`,
    description: `Definitions of key terms used in this article about ${post.tags[0]}`,
    url: `https://integrated.social/blog/${post.slug}/`,
    inLanguage: "en-GB",
    publisher: {
      "@type": "Organization",
      "@id": "https://integrated.social/#organization",
    },
    hasDefinedTerm: postDefinedTerms.map((term, i) => ({
      "@type": "DefinedTerm",
      "@id": `https://integrated.social/blog/${post.slug}/#term-${i + 1}`,
      name: term.name,
      description: term.description,
      inDefinedTermSet: `https://integrated.social/blog/${post.slug}/#glossary`,
    })),
  } : null;

  // ── QAPage schema ───────────────────────────────────────────────────────────
  // More specific than FAQPage for conversational AI extraction of comparison queries.
  // Applied to posts where the article is structured as a comparison or direct Q&A.
  const qaPageSlugs = [
    "chatgpt-vs-gemini-vs-claude-vs-manus-ai-assistant-comparison-2026",
    "ai-search-replacing-google-or-making-it-more-powerful-2026",
  ];
  const qaPageSchema = qaPageSlugs.includes(post.slug) && post.faqSchema.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "QAPage",
    "@id": `https://integrated.social/blog/${post.slug}/#qapage`,
    name: post.title,
    description: post.metaDescription,
    url: `https://integrated.social/blog/${post.slug}/`,
    mainEntity: post.faqSchema.slice(0, 3).map((faq) => ({
      "@type": "Question",
      name: faq.question,
      text: faq.question,
      answerCount: 1,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
        author: { "@id": "https://integrated.social/modi-elnadi/#person" },
        dateCreated: post.date,
        upvoteCount: 1,
        url: `https://integrated.social/blog/${post.slug}/`,
      },
    })),
  } : null;

  // HowTo schema - generated when post.howToSteps is populated
  type HowToStep = { name: string; text: string; url?: string };
  type PostWithHowTo = typeof post & { howToSteps?: HowToStep[] };
  const postWithHowTo = post as PostWithHowTo;
  const howToSchema = post.slug !== "openai-openads-optimizer-testflight-warning-2026" && postWithHowTo.howToSteps && postWithHowTo.howToSteps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": `https://integrated.social/blog/${post.slug}/#howto`,
    name: post.title,
    description: post.metaDescription,
    author: { "@id": "https://integrated.social/modi-elnadi/#person" },
    publisher: { "@id": "https://integrated.social/#organization" },
    step: postWithHowTo.howToSteps!.map((step: HowToStep, i: number) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: step.name,
      text: step.text,
      ...(step.url ? { url: step.url } : {}),
    })),
  } : null;

  // FAQ schema remains a separate block for legacy posts. New source-qualified
  // releases can opt into a single page-scoped graph via singleGraphSchema.
  const faqSchema = !post.singleGraphSchema && faqSchemaNode ? {
    "@context": "https://schema.org",
    ...faqSchemaNode,
  } : null;


  return (
    <>
      <SEOHead
        title={post.metaTitle || `${post.title} | Integrated.Social`}
        description={post.metaDescription}
        canonical={`https://integrated.social/blog/${post.slug}/`}
        ogImage={resolveSocialPreviewImage(post.socialImage ?? post.heroImage)}
        {...(exactExpedia ? { ogImageWidth: 2560, ogImageHeight: 1440, ogImageType: "image/png", ogImageAlt: post.heroAlt } : {})}
        {...(post.slug === QUERY_VOLUME_SLUG ? { ogImageWidth: 1600, ogImageHeight: 900, ogImageType: "image/jpeg", ogImageAlt: post.heroAlt } : {})}
        keywords={post.tags.join(", ")}
        locale={post.locale ?? "en"}
        article={{
          publishedTime: post.datePublished ?? post.date,
          modifiedTime: post.dateModified ?? post.updatedDate ?? post.date,
          author: post.author,
          section: post.tags[0],
          tags: post.tags,
        }}
      />
      <SchemaScript schema={articleSchema} />
      {faqSchema && <SchemaScript schema={faqSchema} />}
      {howToSchema && <SchemaScript schema={howToSchema} />}
      {definedTermSchema && <SchemaScript schema={definedTermSchema} />}
      {qaPageSchema && <SchemaScript schema={qaPageSchema} />}

      <Header />
      <main className="min-h-screen bg-background pt-24">
        {/* Blog Search Bar - persists on article pages */}
        <BlogSearchBar />

        <Breadcrumbs
          items={visualBreadcrumbItems}
          includeSchema={false}
          className="container max-w-4xl mx-auto px-4 py-4"
        />

        {/* Article Header */}
        <header className="container max-w-4xl mx-auto px-4 py-8 md:py-12">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {post.tags.map((tag) => {
              const archive = getBlogTopicArchiveByTag(tag);
              const badge = (
                <Badge variant="secondary" className="bg-amber-500/10 text-amber-700 transition-colors">
                  {tag}
                </Badge>
              );

              // Controlled editorial topics use canonical, crawlable archive links.
              // Fine-grained legacy labels remain visible context, but do not create
              // internal links to intentionally noindexed filter results.
              return archive ? (
                <Link key={tag} href={blogTopicArchivePath(archive)} className="hover:opacity-80">
                  {badge}
                </Link>
              ) : (
                <span key={tag}>{badge}</span>
              );
            })}
          </div>

          <h1 id="article-title" className="font-serif text-3xl md:text-5xl tracking-tight text-foreground mb-6 leading-tight">
            {post.h1 ?? post.title}
          </h1>

          {exactEditorial && renderArticleByline()}

          <p className="article-intro text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
            {post.excerpt}
          </p>

          {(post.nonAffiliationNotice || post.securityReportingCallout) && (
            <div className="space-y-4 mb-8">
              {post.nonAffiliationNotice && (
                <SecurityNonAffiliationNotice {...post.nonAffiliationNotice} />
              )}
              {post.securityReportingCallout && <ReportSuspiciousInvitationCallout />}
            </div>
          )}

          {post.slug === "transparent-aeo-evidence-based-ai-visibility-scoring-2026" && (
            <AEOScorecardLeadMagnet postSlug={post.slug} />
          )}

          {post.slug === "us-china-ai-talks-superintelligence-marketing-manufacturing-jobs-2026" && (
            <USChinaAIBriefingLeadMagnet postSlug={post.slug} />
          )}

          {!exactEditorial && renderArticleByline()}

        </header>

        {!exactEditorial && summaryPlacement === "top" && (
          <div className="container max-w-4xl mx-auto px-4 pb-8">
            {renderAiSummary()}
          </div>
        )}

        {!exactEditorial && renderArticleMedia()}

        {/* Default AI summary placement for articles without a top TL;DR treatment. */}
        {!exactEditorial && summaryPlacement === "after-hero" && (
          <div className="container max-w-4xl mx-auto px-4 mb-10">
            {renderAiSummary()}
          </div>
        )}

        {exactEditorial ? (
          <div className="container max-w-4xl mx-auto px-4 pb-8">
            <section className="ai-summary-block rounded-xl border border-[#00D4AA]/30 bg-[#00D4AA]/5 px-5 py-4" aria-labelledby="article-tldr-heading">
              {renderHeadingAliases("article-tldr-heading")}<h2 id="article-tldr-heading" className="font-semibold text-foreground mb-3">Quick Summary</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed text-muted-foreground">
                {post.aiSummary?.map((point) => <li key={point}>{point}</li>)}
              </ul>
            </section>
            <nav aria-label="Table of contents" className="mt-5 rounded-xl border border-border bg-muted/30">
              <details>
                <summary className="cursor-pointer px-5 py-3 font-semibold text-foreground focus-visible:ring-2 focus-visible:ring-amber-500">In this article</summary>
                <ol className="px-5 pb-4 space-y-2 text-sm">
                  {parseTocItems(post.content).filter((item) => item.level === 2).map((item) => (
                    <li key={item.id}><a href={`#${item.id}`} className="underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-amber-500">{item.text}</a></li>
                  ))}
                  <li><a href={`#${ARTICLE_FAQ_SECTION_ID}`} className="underline underline-offset-2">Frequently asked questions</a></li>
                  <li><a href={exactExpedia ? "#when-should-you-involve-an-ai-marketing-agency" : "#when-should-a-b2b-team-involve-an-ai-marketing-agency"} className="underline underline-offset-2">{exactExpedia ? "When should you involve an AI marketing agency?" : "When should a B2B team involve an AI marketing agency?"}</a></li>
                </ol>
              </details>
            </nav>
          </div>
        ) : (<>
        {/* Smaller articles retain this inline navigation at every breakpoint; long-form pillar guides swap to the desktop rail at xl. */}
        <div className="container max-w-4xl mx-auto px-4">
          <TableOfContents content={post.content} variant="inline" showOnDesktop={!shouldUseStickyPillarToc} />
        </div>
        </>)}
        {/* Affiliate Promo Box - Placement 2: after hero image, before article body */}
        {!exactEditorial && post.promoBox && (
          <div className="container max-w-4xl mx-auto px-4 mb-10">
            <a
              href={canonicalPath(post.promoBox.ctaUrl)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (post.promoBox?.ctaUrl.includes("manus.im/invitation/")) {
                  trackManusReferralClick({
                    post_slug: post.slug,
                    placement: "post_hero_promo",
                    cta_text: post.promoBox.ctaText,
                    scroll_depth_at_click: scrollDepth,
                  });
                }
              }}
              className="block rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent hover:from-amber-500/18 hover:border-amber-500/70 transition-all duration-200 group"
            >
              <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  {post.promoBox.badge && (
                    <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500 text-white uppercase tracking-wider">
                      {post.promoBox.badge}
                    </span>
                  )}
                  <p className="font-serif text-foreground text-lg leading-snug mb-1.5">{post.promoBox.headline}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{post.promoBox.body}</p>
                </div>
                <div className="shrink-0">
                  <span className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-500 text-white font-semibold group-hover:bg-amber-600 transition-colors whitespace-nowrap shadow-lg shadow-amber-500/25">
                    {post.promoBox.ctaText}
                  </span>
                </div>
              </div>
            </a>
          </div>
        )}

        {/* Explicit editorial promo boxes retain their single approved placement.
            This fallback gives the legacy archive a consistent in-body next step. */}
        {!exactEditorial && !post.promoBox && (
          <div className="container max-w-4xl mx-auto px-4 mb-10">
            <section
              data-testid="blog-score-lead-magnet"
              aria-labelledby="blog-score-lead-magnet-heading"
              className="rounded-2xl border border-[#00D4AA]/35 bg-gradient-to-r from-[#00D4AA]/10 via-[#00D4AA]/5 to-transparent px-6 py-5"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <span className="inline-block mb-2 rounded-full bg-[#00D4AA]/20 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-[#007f69] dark:text-[#5ee6cb]">
                    Free AI Visibility Score
                  </span>
                  <h2 id="blog-score-lead-magnet-heading" className="font-serif text-xl leading-snug text-foreground">
                    See how clearly your site can be understood in AI search
                  </h2>
                  <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                    Receive a client-safe score and a high-level evidence summary for your top pages. It is a starting point for discussion, not a guarantee of citations, rankings or leads.
                  </p>
                </div>
                <Link
                  href="/free-ai-growth-audit/"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#00A98F] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#00A98F]/20 transition-colors hover:bg-[#008f78] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4AA] focus-visible:ring-offset-2"
                >
                  Check my AI visibility
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </section>
          </div>
        )}

        {/* Animated KPI Stats (when post has structured stat data) */}
        {post.stats && post.stats.length > 0 && (
          <div className="container max-w-4xl mx-auto px-4 mb-12">
            <AnimatedStatGrid
              eyebrow="Key Numbers"
              stats={post.stats}
              cols={post.stats.length >= 4 ? 4 : (post.stats.length as 2 | 3 | 4)}
              className="overflow-hidden"
              interactive={hasInteractiveArticleMedia}
            />
          </div>
        )}

        {/* Source-qualified explanatory visuals - render as real figures so
            semantic ALT text is available in the server-rendered artifact. */}
        {post.slug === "saudi-ai-giga-campus-humain-nebius-datavalt-compute-economics-2026" && (
          <SaudiAiGigaCampusInfographic />
        )}

        {post.slug === "ai-powered-abm-enterprise-b2b-2026" && (
          <BuyerArgumentMatrix />
        )}
        {post.slug === "anthropic-ipo-s1-claude-compute-economics-2026" && (
          <AnthropicIpoFilingInfographic />
        )}

        {post.inlineVisuals && post.inlineVisuals.length > 0 && (
          <div className="container max-w-4xl mx-auto px-4 mb-12 space-y-8">
            {post.inlineVisuals.map((visual) => (
              <figure key={visual.src} className={`group overflow-hidden rounded-2xl border border-border bg-card shadow-sm ${hasInteractiveArticleMedia ? "transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-0.5 hover:border-[#00D4AA]/45 hover:shadow-[0_18px_42px_rgba(0,212,170,0.14)] motion-reduce:transform-none motion-reduce:transition-none" : ""}`}>
                <ResponsiveImage
                  src={visual.src}
                  alt={visual.alt}
                  className={`w-full h-auto ${hasInteractiveArticleMedia ? "transition-transform duration-500 ease-out group-hover:scale-[1.015] motion-reduce:transform-none motion-reduce:transition-none" : ""}`}
                  width={1200}
                  height={675}
                  sizes="(min-width: 1280px) 896px, (min-width: 768px) calc(100vw - 48px), calc(100vw - 32px)"
                />
                <figcaption className="px-5 py-4 text-sm leading-relaxed text-muted-foreground border-t border-border bg-muted/30">
                  {visual.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        <AIInfrastructureStockChart slug={post.slug} />

        {/* GEO Share Toolbar - only on the GEO methodology post */}
        {post.slug === "geo-measurement-methodology-six-metrics-2026" && (
          <div className="container max-w-4xl mx-auto px-4">
            <GEOShareToolbar slug={post.slug} title={post.title} assessmentResult={geoAssessmentPayload} />
          </div>
        )}

        {/* GPT-5.6 Token Cost Calculator - only on the GPT-5.6 guide */}
        {post.slug === "chatgpt-gpt-56-sol-terra-luna-complete-guide-2026" && (
          <div className="container max-w-4xl mx-auto px-4 mb-12">
            <TokenCostCalculator />
          </div>
        )}

        {/* Long-form pillar guides reserve a desktop reading rail; all other posts keep the inline TOC only. */}
        <div className={[
          "container mx-auto px-4 pb-16",
          shouldUseStickyPillarToc ? "max-w-7xl flex items-start gap-12" : "max-w-4xl",
        ].join(" ")}>
          {shouldUseStickyPillarToc && (
            <TableOfContents content={post.content} variant="sidebar" showOnDesktop />
          )}
          {/* Article body */}
          <article className={shouldUseStickyPillarToc ? "min-w-0 max-w-4xl flex-1" : "min-w-0 max-w-4xl"}>
          {renderArticleSegment(contentBefore, "before")}


          {post.slug === "instinct-personal-agent-trust-alignment-2026" && (
            <PersonalAgentControlsComparison />
          )}

          {/* Mid-article related post card - injected after the third H2 heading */}
          {midArticlePost && contentAfter && (
            <MidArticleCard
              fromSlug={post.slug}
              slug={midArticlePost.slug}
              title={midArticlePost.title}
              excerpt={midArticlePost.excerpt}
              heroImage={midArticlePost.heroImage}
              date={midArticlePost.date}
              tags={midArticlePost.tags}
              content={midArticlePost.content}
              sharedTag={midArticleSharedTag ?? undefined}
            />
          )}

          {contentAfter && (
            renderArticleSegment(contentAfter, "after")
          )}
          {!exactEditorial && !contentAfter && (
            renderArticleSegment(articleContent, "complete")
          )}
          {!exactEditorial && <ClusterPathway slug={post.slug} />}
          </article>
        </div>

        {/* Post-content sections: Pillar links, FAQ, Author, CTA, Share, Related Posts */}
        <article className="container max-w-4xl mx-auto px-4 pb-16">

          {/* Pillar Topic Cross-Links (Hub-and-Spoke Navigation) */}
          {!exactEditorial && pillars.length > 0 && (
            <section className="mt-12 border border-amber-500/20 rounded-xl p-6 bg-amber-500/5">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-amber-600" />
                <h3 className="font-serif text-foreground">
                  Part of: {pillars.map((p) => p.name).join(" & ")}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                This article is part of our {pillars[0].primaryKeyword} topic cluster. Explore related guides:
              </p>
              {siblingPosts.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {siblingPosts.map((sibling) => sibling && (
                    <Link
                      key={sibling.slug}
                      href={`/blog/${sibling.slug}/`}
                      className="flex items-start gap-2 p-3 rounded-lg border border-border hover:border-amber-500/50 hover:bg-amber-500/5 transition-all group"
                    >
                      <ArrowRight className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <span className="text-sm font-medium text-foreground group-hover:text-amber-600 transition-colors line-clamp-2">
                        {sibling.title}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
              <Link href={canonicalPath(pillars[0].slug)} className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors">
                View all {pillars[0].name} content →
              </Link>
            </section>
          )}

          {/* FAQ Section */}
          {post.faqSchema.length > 0 && (
            <section
              className="article-faq mt-16 border-t border-border pt-12"
              aria-labelledby={ARTICLE_FAQ_SECTION_ID}
              data-faq-section-heading={ARTICLE_FAQ_HEADING_HIERARCHY.section.tag}
            >
              <h2 id={ARTICLE_FAQ_SECTION_ID} className="font-serif text-2xl md:text-3xl mb-8">{exactEditorial ? "Frequently asked questions" : "Frequently Asked Questions"}</h2>
              {exactEditorial && <p className="mb-8 text-muted-foreground leading-relaxed" data-testid="article-faq-introduction">{exactPresentation.faqIntroduction}</p>}
              <div className="space-y-6">
                {post.faqSchema.map((faq, i) => (
                  <details key={i} className="group border border-border rounded-lg overflow-hidden" open={i === 0}>
                    <summary className="flex items-center justify-between p-5 cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors">
                      <h3 data-faq-question-heading={ARTICLE_FAQ_HEADING_HIERARCHY.question.tag} className="font-semibold text-foreground pr-4">{faq.question}</h3>
                      <span className="text-muted-foreground group-open:rotate-180 transition-transform duration-200">▼</span>
                    </summary>
                    <div className="p-5 text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {exactEditorial && (
            <>
              {renderArticleSegment(exactPresentation.commissioningContent, "commissioning")}
              <section data-testid="article-readiness-cta" aria-labelledby="article-readiness-heading" className="mt-8 rounded-xl border border-[#00D4AA]/35 bg-[#00D4AA]/5 p-5">
                <h2 id="article-readiness-heading" className="font-serif text-xl text-foreground">{exactPresentation.cta.heading}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{exactPresentation.cta.body}</p>
                <Link href="/free-ai-growth-audit/" className="mt-4 inline-flex min-h-11 items-center rounded-lg bg-[#008f78] px-4 py-2 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-[#00D4AA] focus-visible:ring-offset-2">{exactPresentation.cta.button}</Link>
              </section>
              <div className="mt-10" data-testid="article-retained-media">{renderArticleMedia()}</div>
            </>
          )}

          {/* Visible source context mirrors the source-linked Article schema. */}
          <EvidenceSourceContext references={externalRefs} />

          {/* GEO Readiness Assessment - only on the GEO methodology post */}
          {post.slug === "geo-measurement-methodology-six-metrics-2026" && (
            <GEOReadinessAssessment onComplete={setGeoAssessmentPayload} />
          )}

          {/* Inline Free AI Visibility Score widget - shown on posts with inlineAuditWidget: true */}
          {post.inlineAuditWidget && (
            <section className="mt-16 border-t border-border pt-12">
              <div className="mb-6">
                <span className="inline-block mb-3 px-3 py-1 rounded-full text-xs font-bold tracking-widest bg-amber-500/15 text-amber-500 uppercase">
                  Free AI Visibility Score
                </span>
                <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-2">
                  {exactAiOverviews ? AI_OVERVIEWS_PRESENTATION.cta.heading : "Is your website being cited by ChatGPT, Gemini, and Google AI Mode?"}
                </h2>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  {exactAiOverviews ? AI_OVERVIEWS_PRESENTATION.cta.body : "Enter your website URL below to receive a client-safe AI visibility score and a high-level evidence summary. The score is a starting point for discussion, not a promise of citations, rankings or leads."}
                </p>
              </div>
              <AuditWidget copy={exactAiOverviews ? AI_OVERVIEWS_AUDIT_COPY : undefined} />
            </section>
          )}

          {/* Author Bio and a lightweight copy control keep sharing close to the byline. */}
          <AuthorBio currentSlug={slug} articleTitle={post.title} canonicalUrl={`https://integrated.social/blog/${post.slug}/`} />

          {/* Reverse conversion path: a context-matched service plus the free audit. */}
          {!exactExpedia && !usesBottomRelatedServices && <RelatedServicePathway postSlug={post.slug} path={conversionPath} />}

          {/* Curated high-intent news rollout. The shared registry is also enforced server-side. */}
          {isNewsletterSubscriptionSource(post.slug) && (
            <NewsletterSubscriptionForm sourceSlug={post.slug} />
          )}

          {/* AEO/LLMO Buyer's Guide Lead Capture - renders only on the 5 series posts */}
          {AEO_SERIES_POSTS.some((p) => p.slug === post.slug) && (
            <AEOLeadCaptureForm sourceSlug={post.slug} />
          )}

          {/* AEO/LLMO Buyer's Guide Series Navigation - renders only on the 5 series posts */}
          {AEO_SERIES_POSTS.some((p) => p.slug === post.slug) && (
            <AEOSeriesNav currentSlug={post.slug} />
          )}

          {/* Share This Post - polished button group with LinkedIn, X, WhatsApp, copy-link */}
          {/* sentinel div: FloatingShareRail watches this to hide itself when the end-of-article share section is visible */}
          <div ref={shareRowSentinelRef} />
          <SharePostRow slug={post.slug} title={post.title} excerpt={post.excerpt} heroImage={post.heroImage} />

          {/* Curated editorial carousels provide a card-level rationale; the shared grid remains the default elsewhere. */}
          {articleCarouselContext ? (
            <RelatedAIInsightsCarousel
              posts={articleCarouselPosts}
              currentSlug={post.slug}
              heading={articleCarouselContext.heading}
              description={articleCarouselContext.description}
              relevanceSummaries={articleCarouselSummaries}
              itemListName={articleCarouselContext.itemListName}
              cardLabel={articleCarouselContext.cardLabel}
              previousLabel={articleCarouselContext.previousLabel}
              nextLabel={articleCarouselContext.nextLabel}
            />
          ) : (
            <RelatedPosts
              posts={relatedPosts}
              currentTags={post.tags}
              currentSlug={post.slug}
            />
          )}

          {!exactAiOverviews && <section className="mx-auto mt-10 max-w-4xl px-4" aria-label="Free AI Visibility Score">
            <ScoreInlineForm placement="blog_end" />
          </section>}

          {/* Curated, non-duplicative breaking-news trail - currently used by the Astra analysis. */}
          {relatedNewsPosts.length > 0 && (
            <RelatedNews posts={relatedNewsPosts} currentSlug={post.slug} />
          )}

          {B2B_AGENCY_COMPARISON_RELATED_POSTS.has(post.slug) && <B2BMarketingAgenciesGuideLink />}

          {[
            "should-you-bid-on-your-own-brand-brand-ppc-incrementality-2026",
            "performance-max-vs-smart-campaigns-ai-bidding-strategy-2026",
            "organic-paid-agentic-ai-cpa-ltv",
          ].includes(post.slug) && <PPCOptimisationGuideLink />}

          {LOCAL_INDUSTRY_RELATED_POSTS.has(post.slug) && (
            <LocalIndustryGuideLinks context={LOCAL_INDUSTRY_RELATED_POSTS.get(post.slug) ?? "general"} />
          )}

          {usesBottomRelatedServices && (
            <RelatedServicesSection postSlug={post.slug} services={relatedServices} locale="en" />
          )}

          {/* Amazon Associates - contextual product recommendations */}
          <AmazonProductBox products={articleAmazonProducts} postSlug={post.slug} />
        </article>
      </main>
      {/* Desktop floating left-rail share bar - lg+ only, hidden on mobile */}
      <FloatingShareRail title={post.title} slug={post.slug} excerpt={post.excerpt} sentinelRef={shareRowSentinelRef} />
      {/* Mobile and tablet sticky share controls - desktop uses the vertical rail. */}
      <MobileShareFAB
        title={post.title}
        slug={post.slug}
        excerpt={post.excerpt}
        sentinelRef={shareRowSentinelRef}
      />
      {/* Arabic translation toggle - discreet "ع" button, fixed bottom-right */}
      <ArabicToggle />
      <Footer />
    </>
  );
}

/** Compact search bar shown at the top of every blog article page.
 *  Navigates to /blog?q=... so results appear on the main Blog index. */
function BlogSearchBar() {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setLocation(`/blog?q=${encodeURIComponent(query.trim())}`);
    } else {
      setLocation("/blog");
    }
  };

  return (
    <div className="border-b border-border bg-muted/30">
      <div className="container max-w-4xl mx-auto px-4 py-3">
        <form onSubmit={handleSubmit} className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Search all articles..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-9 text-sm bg-background/60 border-border focus:bg-background transition-colors"
          />
        </form>
      </div>
    </div>
  );
}

// ── CopyLinkButton ────────────────────────────────────────────────────────────
function CopyLinkButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const url = `https://integrated.social/blog/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      aria-label="Copy link"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-muted text-sm font-medium transition-all duration-200"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-green-500" />
          <span className="text-green-600">Copied!</span>
        </>
      ) : (
        <>
          <Link2 className="w-4 h-4" />
          <span>Copy link</span>
        </>
      )}
    </button>
  );
}

/**
 * Mobile/tablet sticky sharing tray. It uses discrete buttons rather than a
 * single platform-dependent action, then yields to the full SharePostRow at
 * the end of the article so sharing controls never compete with one another.
 */
function MobileShareFAB({
  title,
  slug,
  excerpt,
  sentinelRef,
}: {
  title: string;
  slug: string;
  excerpt?: string;
  sentinelRef?: React.RefObject<HTMLElement | null>;
}) {
  const [visible, setVisible] = useState(false);
  const [hiddenBySentinel, setHiddenBySentinel] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);
  const url = `https://integrated.social/blog/${slug}/`;

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setHasNativeShare(typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef?.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHiddenBySentinel(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef]);

  const openSocialShare = (platform: "linkedin" | "whatsapp", destinationUrl: string) => {
    trackBlogShareClick({ platform, post_slug: slug, post_title: title });
    trackOutboundSocialClick({
      social_platform: platform,
      link_location: "mobile_sticky_share_tray",
      destination_url: destinationUrl,
    });
    window.open(destinationUrl, "_blank", "noopener,noreferrer");
  };

  const handleLinkedIn = () => {
    openSocialShare(
      "linkedin",
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    );
  };

  const handleWhatsApp = () => {
    const message = excerpt
      ? `${title}\n\n${excerpt.slice(0, 150)}\n\n${url}`
      : `${title}\n\n${url}`;
    openSocialShare("whatsapp", `https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    trackBlogShareClick({ platform: "copy_link", post_slug: slug, post_title: title, share_method: "icon_button" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, text: excerpt, url });
      trackBlogShareClick({ platform: "native_share", post_slug: slug, post_title: title, share_method: "native_api" });
    } catch {
      // A dismissed native-share sheet is not an error and should stay silent.
    }
  };

  const isShown = visible && !hiddenBySentinel;

  return (
    <div
      role="complementary"
      aria-label="Sticky article sharing controls"
      style={{
        opacity: isShown ? 1 : 0,
        pointerEvents: isShown ? "auto" : "none",
        transition: "opacity 0.2s cubic-bezier(0.23,1,0.32,1), transform 0.2s cubic-bezier(0.23,1,0.32,1)",
        transform: isShown ? "translateY(0)" : "translateY(10px)",
      }}
      className="lg:hidden fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1 rounded-full border border-slate-700/80 bg-slate-950/95 p-1.5 shadow-xl shadow-slate-950/30 backdrop-blur"
      data-testid="mobile-sticky-share-tray"
    >
      <span className="sr-only">Share this article</span>
      <button
        onClick={handleLinkedIn}
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0A66C2] text-white transition-transform duration-150 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0A66C2]"
      >
        <Linkedin className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        onClick={handleWhatsApp}
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform duration-150 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#25D366]"
      >
        <MessageCircle className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        onClick={handleCopy}
        aria-label={copied ? "Article link copied" : "Copy article link"}
        title={copied ? "Copied" : "Copy article link"}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-transform duration-150 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" /> : <Link2 className="h-4 w-4" aria-hidden="true" />}
      </button>
      {hasNativeShare && (
        <button
          onClick={handleNativeShare}
          aria-label="Open device share menu"
          title="More sharing options"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-slate-950 transition-transform duration-150 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
        >
          <Share2 className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
import AmazonProductBox from "@/components/AmazonProductBox";
import { getProductsForArticle } from "@/data/amazonProducts";
import { AIInfrastructureStockChart } from "@/components/AIInfrastructureStockChart";
import AnthropicIpoFilingInfographic from "@/components/AnthropicIpoFilingInfographic";
